import { useState, useCallback, useRef } from 'react';
import { client } from '../api/client';
import type {
  AnalysisJob,
  ConversationAnalysisResult,
  ParsePreview,
} from '../types';
import { InputPanel } from '../components/analysis/InputPanel';
import { ResultsPanel } from '../components/analysis/ResultsPanel';

interface JobState {
  job: AnalysisJob | null;
  preview: ParsePreview | null;
  result: ConversationAnalysisResult | null;
  error: string | null;
}

export function ConversationAnalysisPage(): JSX.Element {
  const [state, setState] = useState<JobState>({
    job: null,
    preview: null,
    result: null,
    error: null,
  });
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (jobId: string) => {
      stopPolling();
      pollingRef.current = setInterval(async () => {
        try {
          const res = await client.get<{ job: AnalysisJob }>(`/analysis/jobs/${jobId}`);
          const job = res.data.job;

          setState((prev) => ({ ...prev, job }));

          if (job.status === 'DONE') {
            stopPolling();
            try {
              const resultRes = await client.get<{ result: ConversationAnalysisResult }>(`/analysis/jobs/${jobId}/result`);
              setState((prev) => ({ ...prev, result: resultRes.data.result }));
            } catch (err) {
              console.error('Failed to fetch result:', err);
            }
          } else if (job.status === 'FAILED') {
            stopPolling();
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 1000);
    },
    [stopPolling]
  );

  const handleAnalyze = useCallback(
    async (text: string, file: File | null, inboundOnly: boolean) => {
      setState({ job: null, preview: null, result: null, error: null });

      try {
        const formData = new FormData();
        if (text) {
          formData.append('text', text);
        }
        if (file) {
          formData.append('file', file);
        }
        formData.append('inboundOnly', String(inboundOnly));

        const res = await client.post<{ job: AnalysisJob; preview: ParsePreview }>('/analysis/jobs', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        setState({ job: res.data.job, preview: res.data.preview, result: null, error: null });
        startPolling(res.data.job.id);
      } catch (err) {
        setState((prev) => ({
          ...prev,
          error: err instanceof Error ? err.message : 'Unknown error',
        }));
      }
    },
    [startPolling]
  );

  const handleCancel = useCallback(async () => {
    if (!state.job) return;

    try {
      await client.post(`/analysis/jobs/${state.job.id}/cancel`);
      stopPolling();
      setState((prev) => ({
        ...prev,
        job: prev.job ? { ...prev.job, status: 'FAILED', cancelled: true, error: 'Отменено пользователем' } : null,
      }));
    } catch (err) {
      console.error('Cancel error:', err);
    }
  }, [state.job, stopPolling]);

  const isRunning = state.job?.status === 'QUEUED' || state.job?.status === 'RUNNING';

  return (
    <div className="h-full bg-surface-base flex overflow-hidden">
      <InputPanel
        onAnalyze={handleAnalyze}
        onCancel={handleCancel}
        isRunning={isRunning}
        preview={state.preview}
      />
      <ResultsPanel job={state.job} result={state.result} error={state.error} />
    </div>
  );
}
